/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import utils from "@utils";
import { InvoiceService } from "@services";

import { Modal, FolderTree, Attachment, Loader } from "../../../../../components";

import "./AttachmentsModal.scss";
import DownloadAttachmentsModal from "./DonwloadAttachmentsModal";

const AttachmentsModal = ({ user, invoiceId, show, onHide, selected }) => {
	const { lang } = global;
	const [loading, setLoading] = useState(false);
	const [showDownload, setShowDownload] = useState(false);

	const [allAttachments, setAllAttachments] = useState(0);
	const [attachments, setAttachments] = useState([]);
	const [treeState, setTreeState] = useState(null);

	const loadAttachments = async () => {
		try {
			setLoading(true);

			const { data } = await InvoiceService.getAllAttachments(invoiceId);

			if (data.length === 0) return;

			const jobs = {};

			data.forEach(attachment => {
				jobs[attachment.JobEventId] = jobs[attachment.JobEventId] || {};
				// eslint-disable-next-line max-len
				jobs[attachment.JobEventId][attachment.JobId] = jobs[attachment.JobEventId][
					attachment.JobId
				] || { JobId: attachment.JobId, position: attachment.JobTitle, officers: {} };
				jobs[attachment.JobEventId][attachment.JobId].officers[attachment.UserId] = jobs[
					attachment.JobEventId
				][attachment.JobId].officers[attachment.UserId] || {
					officer: `${attachment.Officer}`,
					attachments: [],
				};
				jobs[attachment.JobEventId][attachment.JobId].officers[
					attachment.UserId
				].attachments.push({ ...attachment });
			});

			const jobEventsChildren = Object.keys(jobs).map(jobEventId => {
				const jobEvents = jobs[jobEventId];
				let attachmentsCounter = 0;

				const jobEventItems = Object.values(jobEvents).map(jobEvent => {
					const officers = Object.keys(jobEvent.officers).map(officerId => {
						const { officer, attachments: officerAttachments } =
							jobEvent.officers[officerId];
						attachmentsCounter += officerAttachments.length;

						let checked = selected?.JobEventId === undefined ? 1 : 0;
						if (
							selected?.JobEventId === Number(jobEventId) &&
							selected?.OfficerId === Number(officerId) &&
							selected?.JobId === Number(jobEvent.JobId)
						) {
							checked = 1;
						}

						return {
							name: `${officer} (${officerAttachments.length})`,
							data: officerAttachments,
							checked,
						};
					});

					return {
						name: jobEvent.position,
						checked: officers.every(officer => officer.checked === 1)
							? 1
							: officers.some(officer => officer.checked > 0)
								? 0.5
								: 0,
						children: officers,
					};
				});

				return {
					name: `Job #${jobEventId} (${attachmentsCounter})`,
					checked: jobEventItems.every(jobEvent => jobEvent.checked === 1)
						? 1
						: jobEventItems.some(jobEvent => jobEvent.checked > 0)
							? 0.5
							: 0,
					children: jobEventItems,
				};
			});

			const newTreeState = {
				name: `View All Attachments (${data.length})`,
				isOpen: true,
				checked: jobEventsChildren.every(jobEvent => jobEvent.checked === 1)
					? 1
					: jobEventsChildren.some(jobEvent => jobEvent.checked > 0)
						? 0.5
						: 0,
				children: jobEventsChildren,
			};

			setTreeState(newTreeState);
			setAttachments(data);
			setAllAttachments(data.length);
		} catch (error) {
			console.error(error);
			utils.notify({
				type: "error",
				message: lang.t("Error loading invoice attachments"),
			});
		} finally {
			setLoading(false);
		}
	};

	const filterCheckedItemsWithData = state => {
		const filteredItems = [];
		let combinedData = [];

		function recursiveFilter(node) {
			if (node.checked === 1 && node.data) {
				filteredItems.push(node);
				combinedData = combinedData.concat(node.data);
			}
			if (node.children && node.children.length > 0) {
				node.children.forEach(child => recursiveFilter(child));
			}
		}

		recursiveFilter(state);
		return { filteredItems, combinedData };
	};

	const onTreeStateChange = newTreeState => {
		const { combinedData } = filterCheckedItemsWithData(newTreeState);
		setAttachments(combinedData);
	};

	useEffect(() => {
		if (!show) {
			setAttachments([]);
			setTreeState(null);

			return;
		}

		loadAttachments();
	}, [invoiceId, show]);

	return (
		<>
			<Modal
				id="attachments-modal"
				show={show}
				onHide={onHide}
				size="xl"
			>
				<Modal.Header closeButton>
					<Modal.Title>{lang.t("Invoice Attachments")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Choose>
						<When condition={treeState === null}>
							<div className="row text-center text-muted">
								{lang.t("No attachments found")}
							</div>
						</When>
						<Otherwise>
							<section className="attachments-section">
								<nav>
									<If condition={treeState !== null}>
										<FolderTree
											data={treeState}
											onChange={onTreeStateChange}
											initCheckedStatus="custom"
											initOpenStatus="custom"
										/>
									</If>
								</nav>
								<main>
									<If condition={attachments.length > 0}>
										<button
											type="button"
											className="btn btn-link margin-top-12 margin-left-12"
											onClick={() => setShowDownload(true)}
										>
											{lang.t("Download")}&nbsp;
											{allAttachments === attachments.length
												? "All"
												: `(${attachments.length})`}
										</button>
									</If>

									<div className="attachments-list">
										<For
											each="attachment"
											index="idx"
											of={attachments}
										>
											<Attachment
												key={idx}
												document={attachment.Document}
												assetInfo={`${attachment.OfficerRank} ${attachment.Officer} ${utils.formatDate(new Date(`${attachment.Document?.CreateDateUTC}Z`), "en-US", user.TimeZoneId, "2-digit")}`}
											/>
										</For>
									</div>
								</main>
							</section>
						</Otherwise>
					</Choose>

					<Loader active={loading} />
				</Modal.Body>
			</Modal>

			<DownloadAttachmentsModal
				id={invoiceId}
				show={showDownload}
				onClose={() => setShowDownload(false)}
				attachments={attachments}
				user={user}
			/>
		</>
	);
};

AttachmentsModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	invoiceId: PropTypes.string.isRequired,
	user: PropTypes.object.isRequired,
	selected: PropTypes.object,
};

AttachmentsModal.defaultProps = {
	show: false,
	selected: {},
	onHide: () => {},
};

export default AttachmentsModal;
